import { type FieldValues, useController } from 'react-hook-form';

import {
  DropdownSingleSelectAsync,
  type DropdownSingleSelectAsyncProps,
} from '~/chords/DropdownSingleSelectAsync/DropdownSingleSelectAsync';
import { FormField, type FormFieldProps } from '~/chords/FormField';

type FormFieldDropdownSingleSelectAsyncProps<
  T,
  K,
  TFormFields extends FieldValues,
> = Omit<
  FormFieldProps<TFormFields>,
  | 'children'
  | 'dangerouslyOverrideClassName'
  | 'triggerClassName'
  | 'fieldMessageClassName'
  | 'labelAsSpan'
> &
  DropdownSingleSelectAsyncProps<T, K>;

export function FormFieldDropdownSingleSelectAsync<
  T,
  K,
  TFormFields extends FieldValues = FieldValues,
>(props: FormFieldDropdownSingleSelectAsyncProps<T, K, TFormFields>) {
  const {
    name,
    id,
    label,
    disabled,
    readOnly,
    labelAddon,
    showErrorMessage,
    ...dropdownProps
  } = props;
  const {
    field: { ref: _ref, ...fieldWithoutRef },
    fieldState: { invalid },
  } = useController({ name, disabled });

  return (
    <FormField
      name={name}
      id={id}
      label={label}
      disabled={disabled}
      readOnly={readOnly}
      labelAddon={labelAddon}
      showErrorMessage={showErrorMessage}
    >
      <DropdownSingleSelectAsync<T, K>
        {...dropdownProps}
        id={id || name}
        state={invalid ? 'error' : 'default'}
        disabled={disabled || readOnly}
        onChange={(value) => {
          fieldWithoutRef.onChange(value);
          fieldWithoutRef.onBlur();
          dropdownProps.onChange?.(value);
        }}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            fieldWithoutRef.onBlur();
          }

          dropdownProps.onOpenChange?.(isOpen);
        }}
        clearOptions={{
          ...(dropdownProps.clearOptions ?? {}),
          onClear: () => {
            fieldWithoutRef.onChange(null);
            fieldWithoutRef.onBlur();
            dropdownProps.clearOptions?.onClear?.();
          },
        }}
      />
    </FormField>
  );
}

FormFieldDropdownSingleSelectAsync.displayName =
  'FormFieldDropdownSingleSelectAsync';
